import amplitude from 'amplitude-js';
import Cookies from 'js-cookie';
import { getFreeToolUsage, isKnownFreeToolCounter, safeFreeToolEventName } from '../utility/analytics/freeToolPrivacy';
import {
  getLogRocketSessionURL,
  identifyLogRocketUser,
  initLogRocket,
  trackLogRocketEvent,
} from '../utility/analytics/logRocket';
import { buildEnvelope, enqueueAnalyticsEvent, initFirehoseSink } from '../utility/analytics/firehoseSink';
import { createPublicPageviewGate, isAutomatedTraffic, isCoreAppRoute } from '../utility/analytics/amplitudePageviews';

const ANALYTICS_ANONYMOUS_ID_STORAGE_KEY = 'qwa_analytics_anonymous_id';

const isBrowser = () => typeof window !== 'undefined';

const isFreeToolPage = () => isBrowser() && getFreeToolUsage({}, window.location.pathname) !== null;

const getAmplitudeKey = () => process.env.NEXT_PUBLIC_AMPLITUDE_KEY || '';

const generateAnonymousId = () => {
  if (!isBrowser()) return `anon_${Date.now()}`;

  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `anon_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

// In-memory fallback for environments where localStorage throws (Safari private,
// strict storage policies, sandboxed iframes). Keeps the shim from taking down
// app startup when storage is unavailable.
let memoryAnonymousId: string | null = null;

const safeStorageGet = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (key: string, value: string): boolean => {
  if (!isBrowser()) return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const getAnonymousId = () => {
  if (!isBrowser()) return generateAnonymousId();

  const existingId = safeStorageGet(ANALYTICS_ANONYMOUS_ID_STORAGE_KEY);
  if (existingId) {
    memoryAnonymousId = existingId;
    return existingId;
  }
  if (memoryAnonymousId) return memoryAnonymousId;

  const newAnonymousId = generateAnonymousId();
  safeStorageSet(ANALYTICS_ANONYMOUS_ID_STORAGE_KEY, newAnonymousId);
  memoryAnonymousId = newAnonymousId;
  return newAnonymousId;
};

const setAnonymousId = (anonymousId?: string) => {
  if (!isBrowser()) return;

  const nextAnonymousId = anonymousId || generateAnonymousId();
  safeStorageSet(ANALYTICS_ANONYMOUS_ID_STORAGE_KEY, nextAnonymousId);
  memoryAnonymousId = nextAnonymousId;
};

let amplitudeReady = false;
let knownUserId: string | null = null;
let amplitudeUserId: string | null = null;
let logRocketSessionURL: string | null = null;
let pendingPageview: ReturnType<typeof createPublicPageviewGate> | null = null;

export const cancelPendingAmplitudePageview = () => {
  pendingPageview?.cancel();
  pendingPageview = null;
};

const ensureAmplitudeReady = () => {
  // Do not initialize the SDK for crawlers: init can flush persisted events.
  if (isAutomatedTraffic()) return false;
  if (amplitudeReady) return true;
  const amplitudeKey = getAmplitudeKey();
  if (!amplitudeKey) return false;

  amplitude.getInstance().init(amplitudeKey, null, {
    deviceId: getAnonymousId(),
    // The legacy SDK switches to beacon transport for this callback. Flush an
    // early click/pageview before navigation can cancel the in-flight HTTP send.
    onExitPage: () => {
      if (!isAutomatedTraffic()) amplitude.getInstance().sendEvents();
    },
  });
  amplitudeReady = true;
  if (logRocketSessionURL && !isFreeToolPage()) {
    amplitude.getInstance().setUserProperties({ logRocketSessionURL });
  }
  return true;
};

const setAmplitudeUserId = (userId: string) => {
  if (!userId) return;
  // Identity also belongs to Firehose and must survive an Amplitude opt-out.
  knownUserId = userId;
  if (!ensureAmplitudeReady() || amplitudeUserId === userId) return;
  amplitude.getInstance().setUserId(userId);
  amplitudeUserId = userId;
};

const initAmplitudeClient = (userId: string | null = null) => {
  if (userId) {
    setAmplitudeUserId(userId);
    pendingPageview?.flush();
  } else {
    ensureAmplitudeReady();
  }
};

const buildIdentifyPayload = (traits: Record<string, any> = {}) => {
  const identify = new amplitude.Identify();

  Object.entries(traits).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      identify.set(key, value);
    }
  });

  return identify;
};

const attachLogRocketSessionToAmplitude = () => {
  getLogRocketSessionURL((sessionURL) => {
    if (!sessionURL) return;
    logRocketSessionURL = sessionURL;
    // A replay callback must not initialize Amplitude on an anonymous bounce.
    if (!amplitudeReady || isAutomatedTraffic() || isFreeToolPage()) return;
    amplitude.getInstance().setUserProperties({ logRocketSessionURL: sessionURL });
  });
};

/**
 * Install the `window.analytics` shim (Segment API surface) backed by Amplitude
 * for events and LogRocket for session replay. Idempotent - re-calling returns
 * the existing shim. Returns null in SSR contexts.
 *
 * Bootstrapped from `pages/_app.tsx#componentDidMount`. Once installed, the
 * existing ~300 call sites of `window.analytics.track / identify / page` keep
 * working unchanged.
 */
export const installWindowAnalyticsShim = () => {
  if (!isBrowser()) return null;

  if (window.analytics?.__qwaAmplitudeShim) {
    return window.analytics;
  }

  const analyticsShim = {
    __qwaAmplitudeShim: true,
    track: (eventName: string, properties: Record<string, any> = {}) => {
      if (!eventName) return;
      const usage = getFreeToolUsage({ event: eventName, properties }, window.location.pathname);
      const recordedEvent = usage ? safeFreeToolEventName(eventName) : eventName;
      const recordedProperties = usage || properties;
      if (ensureAmplitudeReady()) {
        amplitude.getInstance().logEvent(recordedEvent, recordedProperties);
      }
      trackLogRocketEvent(recordedEvent, recordedProperties);
      enqueueAnalyticsEvent(
        buildEnvelope({
          type: 'track',
          anonymousId: getAnonymousId(),
          userId: knownUserId,
          event: recordedEvent,
          properties: recordedProperties,
        })
      );
    },
    identify: (userIdOrTraits?: string | Record<string, any>, maybeTraits: Record<string, any> = {}) => {
      const userId = typeof userIdOrTraits === 'string' ? userIdOrTraits : null;
      const traits = typeof userIdOrTraits === 'string' ? maybeTraits : userIdOrTraits || {};
      const usage = getFreeToolUsage({ traits }, window.location.pathname);
      const recordedTraits = usage ? {} : traits;
      if (typeof traits.anonymousId === 'string' && traits.anonymousId) {
        setAnonymousId(traits.anonymousId);
      }
      if (userId) setAmplitudeUserId(userId);
      if (ensureAmplitudeReady()) {
        amplitude.getInstance().identify(buildIdentifyPayload(recordedTraits));
      }
      if (userId) pendingPageview?.flush();
      if (userId) identifyLogRocketUser(userId, recordedTraits);
      enqueueAnalyticsEvent(
        buildEnvelope({
          type: 'identify',
          anonymousId: getAnonymousId(),
          userId: userId || knownUserId,
          ...(usage ? { properties: usage } : {}),
          traits: recordedTraits,
        })
      );
    },
    page: (name?: string, properties: Record<string, any> = {}) => {
      const pageProperties =
        typeof name === 'string'
          ? {
              page_name: name,
              ...properties,
            }
          : {
              ...(name || {}),
            };

      const pageContext = {
        ...pageProperties,
        path: window.location.pathname,
        url: window.location.href,
        title: document.title,
      };
      const usage = getFreeToolUsage({ properties: pageContext }, window.location.pathname);

      // Query strings and fragments can carry email addresses or auth tokens.
      // Capture the safe URL now, before a deferred pageview can be released.
      const amplitudePageUrl = window.location.origin + window.location.pathname;
      cancelPendingAmplitudePageview();
      const sendPageview = (entryTrigger?: 'dwell_3s' | 'interaction' | 'identified') => {
        pendingPageview = null;
        try {
          if (ensureAmplitudeReady()) {
            amplitude.getInstance().logEvent('Page Viewed', {
              ...(usage || { ...pageContext, url: amplitudePageUrl }),
              ...(!usage && entryTrigger ? { entry_trigger: entryTrigger } : {}),
            });
          }
        } catch {
          // Deferred analytics must not raise an uncaught timer error or prevent
          // the other sinks from receiving an identity transition.
        }
      };

      if (!isAutomatedTraffic()) {
        if (knownUserId || isCoreAppRoute(window.location.pathname)) {
          sendPageview();
        } else {
          pendingPageview = createPublicPageviewGate(sendPageview);
        }
      }
      enqueueAnalyticsEvent(
        buildEnvelope({
          type: 'page',
          anonymousId: getAnonymousId(),
          userId: knownUserId,
          properties: usage || pageContext,
        })
      );
    },
    reset: () => {
      cancelPendingAmplitudePageview();
      knownUserId = null;
      amplitudeUserId = null;
      setAnonymousId();
      if (!amplitudeReady || !ensureAmplitudeReady()) return;
      amplitude.getInstance().setDeviceId(getAnonymousId());
      amplitude.getInstance().setUserId(null);
    },
    setAnonymousId: (anonymousId: string) => {
      setAnonymousId(anonymousId);
    },
    user: () => ({
      anonymousId: () => getAnonymousId(),
      traits: () => ({}),
    }),
  };

  window.analytics = analyticsShim;
  if (knownUserId || isCoreAppRoute(window.location.pathname)) ensureAmplitudeReady();
  initLogRocket();
  attachLogRocketSessionToAmplitude();
  initFirehoseSink();

  return analyticsShim;
};

export const COMPONENT_NAMES = {
  growth_trends: '01 - Growth Trends',
  competitive_trends: '02 - Competitive Trends',
  ranking_factors: '03 - Ranking Factors',
  content_recommendations: '04 - Content Recommendations',
  usability_recommendations: '05 - Usability Recommendations',
  adhoc_analytics: '06 - Adhoc Analytics',
  internal_linking_recommendations: '07 - Internal Linking Recommendations',
  manage_urls: '08 - Manage URLs',
  manage_queries: '09 - Manage Queries',
  settings: '10 - Settings',
  opportunities: '11 - Opportunities',
  integrations: '12 - Integrations',
  create_content: '13 - Create Content',
  intent_management: '14 - Intent Management',
  category_management: '15 - Category Management',
  manage_list: '16 - Manage List',
  manage_rule: '17 - Manage Rule',
  plan_keywords: '18 - Plan Keywords',
  contentai_home: '19 - Content AI Home',
  contentai_ai_tools: '20 - Content AI Tools',
  ai_visibility: '21 - AI Visibility',
  market_share: '22 - Market Share',
  autonomous_internal_linking: 'Autonomous Internal Linking',

  improve_keyword_relevance: 'Improve Keyword Relevance',
  improve_keyword_relevance_suggest_copy: 'Improve Keyword Relevance Suggest Copy',
  improve_content_quality: 'Improve Content Quality',
  improve_content_quality_suggest_copy: 'Improve Content Quality Suggest Copy',
};

export { CONTENT_AI_TOOL_NAME } from '../utility/analytics/freeToolPrivacy';

export const CONTENT_AI_TOOL_CATEGORY = {
  free_seo_tools: 'Free SEO Tools',
  ai_content_writing_tools: 'AI Content Writing Tools',
  ai_content_optimization: 'AI Content Optimization',
  keyword_research: 'Keyword Research',
};

export const SCREEN_NAMES = {
  growth_trends: 'Growth Trends',
  competitive_trends: 'Competitive Trends',
  trends_watchlist: 'Trends Watchlist',
  ranking_factors: 'Ranking Factors',
  content_recommendations: 'Content Recommendations',
  paid_recommendations: 'Paid Recommendations',
  organic_recommendations: 'Organic Recommendations',
  usability_recommendations: 'Usability Recommendations',
  adhoc_analytics: 'Adhoc Analytics',
  internal_linking_recommendations: 'Internal Linking Recommendations',
  autonomous_internal_linking: 'Autonomous Internal Linking',
  manage_urls: 'Manage URLs',
  manage_queries: 'Manage Queries',
  settings: 'Settings',
  opportunity_explorer: 'Opportunity Explorer',
  top_opportunities: 'Top Opportunities',
  integrations: 'Integrations',
  add_new_page: 'Add New Page', //// optimized new page
  page_draft: 'Page Draft',
  keyword_list: 'Keywords List',
  intent_settings: 'Intent Settings',
  intent_keyword: 'Intent Keyword',
  category_setting: 'Category Settings',
  category_page: 'Category Page',
  taxonomy_designer: 'Taxonomy Designer',
  entity_setting: 'Entity Settings',
  rule_setting: 'Rule Settings',
  connection_setting: 'Connection Settings',
  contentai_home_skip_gsc: 'Skip GSC',
  contentai_subscription_management: 'Content AI Subscription Management',
  contentai_ai_tools: 'Content AI Tools',
  ai_visibility_home: 'AI Visibility Home',
  ai_visibility_trends: 'AI Visibility Trends',
  ai_visibility_drilldown: 'AI Visibility Drilldown',
  ai_visibility_trackers: 'AI Visibility Trackers',
  market_share_trackers: 'Market Share Trackers',
};
interface TrackEventOptions {
  isAddCount?: boolean;
  counterName?: string;
  eventName?: string;
  eventFor?: string;
  payload?: any;
  buttonText?: string;
  user?: any;
  isAnonymousUser?: boolean;
}

export const trackAnalyticsEvent = ({
  isAddCount = false,
  counterName = '',
  eventName = '',
  eventFor = 'Button',
  payload = {},
  buttonText = '',
  user = undefined,
  isAnonymousUser = false,
}: TrackEventOptions) => {
  try {
    const analyticsClient = installWindowAnalyticsShim();
    let userEventName = eventName;
    const counterEventName = counterName ? counterName : `${payload?.label}`;
    // pass eventFor as non button if you want eventName as it is
    if (eventFor?.toLowerCase() === 'button') {
      userEventName = buttonText
        ? `${payload?.label ? payload.label + ' - ' : ''}${buttonText}${
            payload?.screen_name ? ' - ' + payload?.screen_name : ''
          }`
        : `${payload?.label || ''}${payload?.screen_name ? ' - ' + payload?.screen_name : ''}`;
    }
    if (isAddCount) {
      if (isAnonymousUser) {
        setPublicAccessUserAmplitudeInstance();
      }
      incrementCounterEvent(
        counterEventName,
        user,
        getFreeToolUsage({ event: userEventName, properties: payload }) !== null
      );
    }
    if (analyticsClient?.track) {
      let qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
      if (qttr_tools_daypass_auth) {
        analyticsClient.track(userEventName, {
          ...payload,
          is_day_pass_user: true,
        });
      } else {
        analyticsClient.track(userEventName, {
          ...payload,
        });
      }
    }

    // Amplitude track event
    //  amplitudeModule.track(userEventName, payload);
  } catch (error) {
    console.error('trackEvent:: error::', error);
  }
};

export const segmentEventApi = async (userEventName, payload, endpoint) => {
  const analyticsClient = installWindowAnalyticsShim();
  if (!analyticsClient) return;

  if (endpoint === 'identify') {
    analyticsClient.identify(payload?.email, payload || {});
    return;
  }

  analyticsClient.track(userEventName, payload || {});
};

//// Do not delet for now
// const incrementCount = (counterEventName) => {
//   try {
//     // using segmentio, but its use cach/storage from browser
//     // Retrieve current counts from user properties
//     const userProperties = window.analytics.user().traits();

//     const currentCount = userProperties[counterEventName] || 0;
//     const newCount = currentCount + 1;
//     userProperties[counterEventName] = newCount;

//     // Set the updated user properties
//     window.analytics.identify(userProperties);
//     return newCount;
//   } catch (error) {
//     console.error('incrementCount:: error::', error);
//   }
// };

export const setAmplitudeInstance = (email) => {
  // This is use for counter event
  initAmplitudeClient(email);
};

export const setPublicAccessUserAmplitudeInstance = () => {
  initAmplitudeClient(null);
};

export const incrementCounterEvent = (counterEventName, user, isFreeTool = false) => {
  if ((isFreeTool || isFreeToolPage()) && !isKnownFreeToolCounter(counterEventName)) return;
  if (!ensureAmplitudeReady()) return;
  if (user && user.email) {
    initAmplitudeClient(null);
  }
  const identify =
    user && user.email
      ? buildIdentifyPayload({ email: user.email }).add(counterEventName, 1)
      : new amplitude.Identify().add(counterEventName, 1);
  amplitude.getInstance().identify(identify);
};

export const setUserAttribute = (propertyName, value) => {
  if (isFreeToolPage()) return;
  if (!ensureAmplitudeReady()) return;
  const identify = new amplitude.Identify().set(propertyName, value);
  amplitude.getInstance().identify(identify);
};

export const embedEventsToBeTracked = [
  'dashboard:loaded',
  'dashboard:run:start',
  'dashboard:run:complete',
  'dashboard:save:complete',
  'dashboard:edit:start',
  'dashboard:filters:changed',
  'look:run:start',
  'look:run:complete',
  'look:save:complete',
  // 'page:changed',
  'explore:loaded',
  'explore:run:start',
  'explore:run:complete',
  'explore:save:complete',
  'explore:edit:start',
  'explore:filters:changed',
];
